/**
 * Contratos de entrada (Zod) de contabilidad: catálogo de cuentas y política
 * contable, filtros del libro diario, balanza de comprobación, libro mayor,
 * exportación y cierre de períodos.
 */
import { z } from 'zod';
import { ACCOUNT_ROLES, MAX_ACCOUNTING_THRESHOLD_DAYS } from '../accounting/accounts.js';
import { JOURNAL_ENTRY_KINDS } from '../accounting/journal.js';
import { REPORT_FORMATS } from './reports.js';
import { idSchema, isoDateSchema, listQuerySchema, REASON_MIN_LENGTH, TEXT_MAX } from './common.js';
/** Límites de los datos de una cuenta del catálogo. */
const ACCOUNT_LIMITS = {
    codeMax: 30,
    nameMax: 120,
};
/** Código de cuenta: dígitos, letras, puntos y guiones (p. ej. "1201.01" o "1-2-01-01"). */
const ACCOUNT_CODE_PATTERN = /^[0-9A-Za-z.-]+$/;
/** Mensaje de un rango de fechas invertido. */
const INVERTED_RANGE_MESSAGE = 'La fecha inicial no puede ser posterior a la final';
/** Cuenta del catálogo de la institución. */
export const AccountMappingEntrySchema = z.object({
    code: z
        .string()
        .trim()
        .min(1, 'Ingrese el código de la cuenta')
        .max(ACCOUNT_LIMITS.codeMax)
        .regex(ACCOUNT_CODE_PATTERN, 'Use solo dígitos, letras, puntos o guiones'),
    name: z.string().trim().min(3, 'Ingrese el nombre de la cuenta').max(ACCOUNT_LIMITS.nameMax),
});
/** Catálogo completo: una cuenta por cada rol. */
export const AccountMappingSchema = z.object(Object.fromEntries(ACCOUNT_ROLES.map((role) => [role, AccountMappingEntrySchema])));
/** Umbral de días de atraso de la política contable. */
const thresholdDaysSchema = z.number().int().min(0).max(MAX_ACCOUNTING_THRESHOLD_DAYS);
/** Política contable y catálogo de cuentas de la institución. */
export const AccountingSettingsSchema = z.object({
    accounts: AccountMappingSchema,
    pastDueThresholdDays: thresholdDaysSchema,
    nonAccrualThresholdDays: thresholdDaysSchema,
    feesIncludeVat: z.boolean(),
});
/** Rango de fechas con validación de orden. */
const orderedRange = (range) => !range.from || !range.to || range.from <= range.to;
/** Filtros del libro diario (listado de partidas). */
export const journalListQuerySchema = listQuerySchema
    .extend({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    kind: z.enum(JOURNAL_ENTRY_KINDS).optional(),
    branchId: idSchema.optional(),
    /** Solo partidas con alguna línea en esta cuenta. */
    accountCode: z.string().trim().max(ACCOUNT_LIMITS.codeMax).optional(),
    loanId: idSchema.optional(),
})
    .refine(orderedRange, { message: INVERTED_RANGE_MESSAGE, path: ['from'] });
/** Filtros de la balanza de comprobación (y de la exportación del diario). */
export const trialBalanceQuerySchema = z
    .object({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    branchId: idSchema.optional(),
    format: z.enum(REPORT_FORMATS).default('json'),
})
    .refine(orderedRange, { message: INVERTED_RANGE_MESSAGE, path: ['from'] });
/** Filtros del libro mayor de una cuenta. */
export const ledgerQuerySchema = listQuerySchema
    .extend({
    accountCode: z.string().trim().min(1, 'Elija una cuenta').max(ACCOUNT_LIMITS.codeMax),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    branchId: idSchema.optional(),
})
    .refine(orderedRange, { message: INVERTED_RANGE_MESSAGE, path: ['from'] });
/** Reapertura de un período cerrado: exige un motivo que queda en la bitácora. */
export const ReopenAccountingPeriodSchema = z.object({
    reason: z.string().trim().min(REASON_MIN_LENGTH, `Explique el motivo (mínimo ${String(REASON_MIN_LENGTH)} caracteres)`).max(TEXT_MAX.longText),
});
/** Día con que empieza todo período contable (primer día del mes). */
const PERIOD_START_SUFFIX = '-01';
/** Parámetro de ruta de un período contable: su primer día (AAAA-MM-01). */
export const accountingPeriodParamSchema = z.object({
    period: isoDateSchema.refine((date) => date.endsWith(PERIOD_START_SUFFIX), 'El período se indica con el primer día del mes (AAAA-MM-01)'),
});
/** Filtro de agencia de la conciliación. */
export const reconciliationQuerySchema = z.object({ branchId: idSchema.optional() });
//# sourceMappingURL=accounting.js.map